import React, { useState, useCallback } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { FormField, FormTextarea } from './FormField';

const INITIAL_FORM = { name: '', email: '', company: '', phone: '', message: '' };

const CONTACT_CARDS = [
  { icon: Mail, title: 'Email Us', content: <a href="mailto:sales@egeseaou.ee" className="text-amber-600 hover:text-amber-700">sales@egeseaou.ee</a> },
  { icon: MapPin, title: 'HQ Address', content: <p className="text-gray-600">Harju maakond, Tallinn, Kristiine linnaosa, Seebi tn 1-1501, 11316, ESTONIA</p> },
  { icon: Phone, title: 'Call Us', content: <a href="tel:+3726991418" className="text-amber-600 hover:text-amber-700">+372 699 1418</a> },
];

const ContactInfoCard = ({ icon: Icon, title, content }) => (
  <Card className="border-2 hover:border-amber-400 transition-colors">
    <CardContent className="p-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
            <Icon className="w-6 h-6 text-amber-600" />
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
          {content}
        </div>
      </div>
    </CardContent>
  </Card>
);

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    toast({ title: "Message Sent!", description: "Thank you for contacting EGESEA. We'll get back to you soon." });
    setFormData(INITIAL_FORM);
  }, [toast]);

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-amber-50/30 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to discuss your edible oil requirements? Contact us today
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Let&apos;s Start a Conversation
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                Whether you&apos;re looking for bulk orders, custom solutions, or have questions about our products, our team is here to help.
              </p>
            </div>

            <div className="space-y-6">
              {CONTACT_CARDS.map((card) => (
                <ContactInfoCard key={card.title} icon={card.icon} title={card.title} content={card.content} />
              ))}
            </div>

            {/* Image */}
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-xl hidden lg:block bg-white">
              <img
                src="https://customer-assets.emergentagent.com/job_72cdbc16-0f80-4f74-9483-fd8ad1e9a069/artifacts/zogffpm8_egesea%20door.png"
                alt="EGESEA Office"
                className="w-full h-full object-cover" />

            </div>
          </div>

          {/* Contact Form */}
          <Card className="shadow-xl border-2">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <FormField id="name" label="Full Name" required value={formData.name} onChange={handleChange} placeholder="John Doe" />
                <FormField id="email" label="Email Address" type="email" required value={formData.email} onChange={handleChange} placeholder="john@company.com" />
                <FormField id="company" label="Company Name" value={formData.company} onChange={handleChange} placeholder="Your Company" />
                <FormField id="phone" label="Phone Number" type="tel" value={formData.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" />
                <FormTextarea id="message" label="Message" required value={formData.message} onChange={handleChange} placeholder="Tell us about your requirements..." />

                <Button
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold">
                  Send Message
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>);

};

export default Contact;